import { useRef } from 'react'
import type { ReactNode } from 'react'
import { Box, ButtonBase } from '@mui/material'

interface SegmentedControlOption<V extends string> {
  value: V
  label: ReactNode
}

interface SegmentedControlProps<V extends string> {
  options: Array<SegmentedControlOption<V>>
  value: V
  onChange: (value: V) => void
  /** Accessible name for the group (e.g. "Case state"). */
  'aria-label': string
}

/**
 * Enclosed segmented toggle (e.g. Open/Closed): a neutral track with the
 * active segment raised on the paper background. Single-select; Left/Right
 * arrows move the selection like a native radio group.
 */
export const SegmentedControl = <V extends string = string>({
  options,
  value,
  onChange,
  ...rest
}: SegmentedControlProps<V>) => {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])

  const selectByOffset = (offset: number) => {
    const currentIndex = options.findIndex(option => option.value === value)
    const nextIndex = (currentIndex + offset + options.length) % options.length
    const next = options[nextIndex]
    if (!next) return
    onChange(next.value)
    buttonRefs.current[nextIndex]?.focus()
  }

  return (
    <Box
      role="group"
      aria-label={rest['aria-label']}
      sx={theme => ({
        display: 'inline-flex',
        gap: 0.25,
        p: 0.375,
        borderRadius: 1,
        bgcolor: theme.palette.tones.neutral.bg,
      })}
      onKeyDown={event => {
        if (event.key === 'ArrowLeft') selectByOffset(-1)
        if (event.key === 'ArrowRight') selectByOffset(1)
      }}
    >
      {options.map((option, index) => {
        const selected = option.value === value
        return (
          <ButtonBase
            key={option.value}
            ref={element => {
              buttonRefs.current[index] = element
            }}
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            sx={theme => ({
              px: 2,
              py: 0.75,
              // v7 widened theme.shape.borderRadius to `number | string`; this theme sets a number.
              borderRadius: `${Math.max(Number(theme.shape.borderRadius) - 3, 3)}px`,
              fontSize: '1.3rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: theme.transitions.create(['background-color', 'color'], {
                duration: theme.transitions.duration.shortest,
              }),
              ...(selected
                ? {
                    bgcolor: 'background.paper',
                    color: 'primary.main',
                    boxShadow: theme.shadows[2],
                  }
                : {
                    bgcolor: 'transparent',
                    color: 'text.secondary',
                  }),
            })}
          >
            {option.label}
          </ButtonBase>
        )
      })}
    </Box>
  )
}
